import React, { useEffect, useState, useCallback, useRef, useMemo } from 'react'
import { NumericFormat } from 'react-number-format'

import Checkbox from '../../../../../../components/form/Checkbox'

import { CurrencyList, Okei } from '../../../../Plans/Positions/Edit/types'
import OkopfDropdown from './OkopfDropdown'
import OktmoDropdown from './OktmoDropdown'

import MoneyInput from '../../../../../../components/form/MoneyInput'
import LoadingIcon from '../../../../../../components/LoadingIcon'
import {
    CountryList,
    CountryOfOrigin,
    CountryManufacturer,
    Contract,
    ContractPosition,
    Country,
    NationalReestrType,
    BudgetResources,
    ContractSupplier,
    Okopf,
    Oktmo,
} from '../../../types'

import { OrganizationInfo } from '../../../../Org/Home/types'

import { Okpd2 } from '../../../../Org/Handbook/types'

import HoverTooltip from '../../../../../../components/HoverTooltip'
import MultilineSelect from '../../../../../../components/form/MultilineSelect'

import ContractPositionCountryOriginPopup from './ContractPositionCountryOriginPopup'
import ContractPositionCountryManufacturerPopup from './ContractPositionCountryManufacturerPopup'
import { ContractDocument, RegionRf, ContractSupplierRowError } from '../types'

type Props = {
    theme: string
    contractSupplierGuid: string | null
    contractSupplier: ContractSupplier
    handleContractSupplierEditPopup: () => void
    onSave: (patch: Partial<ContractSupplier>) => Promise<void> | void
    oksmList: CountryList[]
    regionRfList: RegionRf[]
    contractAction: boolean
    errors?: ContractSupplierRowError
}

const formatNumber = (num: number | string | null | undefined): string => {
    if (num === null || num === undefined) return '-' // Обрабатываем пустые значения

    const parsedNum = typeof num === 'number' ? num : parseFloat(num)

    if (isNaN(parsedNum)) {
        return '-' // Обрабатываем некорректные значения
    }

    return parsedNum
        .toFixed(2) // Два знака после запятой
        .replace(/\B(?=(\d{3})+(?!\d))/g, ' ') // Разделяем тысячные пробелами
        .replace('.', ',') // Заменяем точку на запятую
}

const ContractSupplierEditPopup: React.FC<Props> = (props) => {
    const textareaRef = useRef<HTMLTextAreaElement>(null)

    const [form, setForm] = useState<ContractSupplier>(props.contractSupplier)

    const [isSaving, setIsSaving] = useState(false)

    const [supplierType, setSupplierType] = useState('')
    const [
        contractSupplierTypeChangePopup,
        setContractSupplierTypeChangePopup,
    ] = useState<boolean>(false)

    const [supplierNonResident, setSupplierNonResident] = useState(false)
    const [
        contractSupplierNonResidentChangePopup,
        setContractSupplierNonResidentChangePopup,
    ] = useState<boolean>(false)

    const okopfInputRef = useRef<HTMLInputElement>(null)
    const okopfPrevRef = useRef<string>('') // что было до открытия
    const okopfPickedRef = useRef<boolean>(false) // выбрали ли пункт

    const [okopfOpen, setOkopfOpen] = useState(false)
    const [okopfItems, setOkopfItems] = useState<Okopf[]>([])

    const okopfDebRef = useRef<ReturnType<typeof setTimeout> | null>(null)
    const [okopfQuery, setOkopfQuery] = useState(form.supplier_okopf ?? '')

    const oktmoInputRef = useRef<HTMLInputElement>(null)
    const oktmoPrevRef = useRef<string>('') // что было до открытия
    const oktmoPickedRef = useRef<boolean>(false) // выбрали ли пункт

    const [oktmoOpen, setOktmoOpen] = useState(false)
    const [oktmoItems, setOktmoItems] = useState<Oktmo[]>([])

    const oktmoDebRef = useRef<ReturnType<typeof setTimeout> | null>(null)
    const [oktmoQuery, setOktmoQuery] = useState(
        form.supplier_address_oktmo ?? ''
    )

    useEffect(() => {
        setForm(props.contractSupplier)
    }, [props.contractSupplier])

    const setField = <K extends keyof ContractSupplier>(
        key: K,
        value: ContractSupplier[K]
    ) => {
        setForm((prev) => ({
            ...prev,
            [key]: value,
        }))
    }

    const handleSave = async () => {
        try {
            setIsSaving(true)
            await props.onSave(form)
        } catch (error) {
            console.error(error)
        } finally {
            setIsSaving(false)
        }
    }

    const handleContractSupplierTypeChangePopup = () => {
        setContractSupplierTypeChangePopup(!contractSupplierTypeChangePopup)
    }

    const handleChangeContractSupplierType = () => {
        setForm((prev) => ({
            ...prev,
            supplier_type: supplierType,
            supplier_non_resident: false,
            supplier_self_employed: false,
            supplier_provider: false,
            supplier_provider_code: null,
            supplier_provider_include_msp_date: null,
            supplier_tax: true,
            supplier_name: null,
            supplier_short_name: null,
            supplier_brand_name: null,
            supplier_okopf: null,
            supplier_okopf_name: null,
            supplier_inn: null,
            supplier_kpp: null,
            supplier_registration_date: null,
            supplier_okpo: null,
            supplier_code: null,
            supplier_address_post_code: null,
            supplier_address_area: null,
            supplier_address_city: null,
            supplier_address_planning_structure: null,
            supplier_address_street: null,
            supplier_address_house: null,
            supplier_address_office: null,
            supplier_address_phone: null,
            supplier_address_email: null,
            supplier_address_country_name: null,
            supplier_address_digital_code: null,
            supplier_address_region_code: null,
            supplier_address_region_name: null,
            supplier_address_oktmo: null,
            supplier_address_oktmo_name: null,
            supplier_address_admin_division: null,
            supplier_address_settlement: null,

            supplier_is_in_order_173n: true, //Нужно будет потом поменять
            supplier_subcontractor: false,
            supplier_subcontractor_code: null,
        }))
        setOkopfQuery('')
        setOktmoQuery('')
        handleContractSupplierTypeChangePopup()
    }

    const handleContractSupplierNonResidentChangePopup = () => {
        setContractSupplierNonResidentChangePopup(
            !contractSupplierNonResidentChangePopup
        )
    }

    const handleChangeContractSupplierNonResident = () => {
        setForm((prev) => ({
            ...prev,
            supplier_non_resident: supplierNonResident,
            supplier_self_employed: false,
            supplier_provider: false,
            supplier_provider_code: null,
            supplier_provider_include_msp_date: null,
            supplier_tax: !supplierNonResident,
            supplier_name: null,
            supplier_short_name: null,
            supplier_brand_name: null,
            supplier_okopf: null,
            supplier_okopf_name: null,
            supplier_inn: null,
            supplier_kpp: null,
            supplier_registration_date: null,
            supplier_okpo: null,
            supplier_code: null,
            supplier_address_post_code: null,
            supplier_address_area: null,
            supplier_address_city: null,
            supplier_address_planning_structure: null,
            supplier_address_street: null,
            supplier_address_house: null,
            supplier_address_office: null,
            supplier_address_phone: null,
            supplier_address_email: null,
            supplier_address_country_name: null,
            supplier_address_digital_code: null,
            supplier_address_region_code: null,
            supplier_address_region_name: null,
            supplier_address_oktmo: null,
            supplier_address_oktmo_name: null,
            supplier_address_admin_division: null,
            supplier_address_settlement: null,

            supplier_is_in_order_173n: true, //Нужно будет потом поменять
            supplier_subcontractor: false,
            supplier_subcontractor_code: null,
        }))
        setOkopfQuery('')
        setOktmoQuery('')
        handleContractSupplierNonResidentChangePopup()
    }

    const fetchOkopf = async (q: string) => {
        // if (!q?.trim()) {
        //     setOkopfItems([])
        //     return
        // }
        const isCode = /^[0-9]/.test(q.trim())
        const url1 = `${process.env.REACT_APP_API_URL}/api/backend/v1/okopf/fast_search?text_query=${encodeURIComponent(q.trim())}`
        const url2 = `${process.env.REACT_APP_API_URL}/api/backend/v1/okopf`
        const url = q == '' ? url2 : url1
        const res = await fetch(url, {
            method: 'GET',
            credentials: 'include',
            headers: { 'Content-Type': 'application/json' },
        })
        if (!res.ok) return
        const data: Okopf[] = await res.json()
        setOkopfItems(data)
    }

    const onOkopfInput = (v: string) => {
        setOkopfQuery(v)
        if (okopfDebRef.current) clearTimeout(okopfDebRef.current)
        okopfDebRef.current = setTimeout(() => {
            fetchOkopf(v)
            setOkopfOpen(true)
        }, 500)
    }

    const fetchOktmo = async (q: string) => {
        // if (!q?.trim()) {
        //     setOktmoItems([])
        //     return
        // }
        const isCode = /^[0-9]/.test(q.trim())
        const url1 = `${process.env.REACT_APP_API_URL}/api/backend/v1/oktmo/fast_search?text_query=${encodeURIComponent(q.trim())}`
        const url2 = `${process.env.REACT_APP_API_URL}/api/backend/v1/oktmo`
        const url = q == '' ? url2 : url1
        const res = await fetch(url, {
            method: 'GET',
            credentials: 'include',
            headers: { 'Content-Type': 'application/json' },
        })
        if (!res.ok) return
        const data: Oktmo[] = await res.json()
        setOktmoItems(data)
    }

    const onOktmoInput = (v: string) => {
        setOktmoQuery(v)
        if (oktmoDebRef.current) clearTimeout(oktmoDebRef.current)
        oktmoDebRef.current = setTimeout(() => {
            fetchOktmo(v)
            setOktmoOpen(true)
        }, 500)
    }

    const errorClass = (hasError?: boolean): string => {
        return hasError ? 'border border-[#C15C5C]' : ''
    }

    return (
        <div className="fixed z-20 top-0 left-0 w-full h-full bg-[#000000] bg-opacity-50 flex justify-center items-center">
            <div
                className="flex flex-col p-5 rounded-lg w-[1200px] h-fit gap-y-5 bg-[#e5e5e5] dark:bg-[#262427]"
                onClick={(e) => e.stopPropagation()}
            >
                <div className="flex flex-row w-full gap-5">
                    <div className="flex flex-col w-[580px] gap-y-5">
                        <h2 className="text-[#181818] dark:text-[#FFFFFF] text-[16px]">
                            Данные о контрагенте
                        </h2>
                        <div className="flex flex-col w-full gap-3">
                            <div className="flex flex-row w-full gap-x-5 ">
                                <div className="flex flex-col min-w-[200px]">
                                    <Checkbox
                                        label="Юридическое лицо"
                                        checked={
                                            form.supplier_type === 'L' || false
                                        }
                                        onChange={(checked) => {
                                            handleContractSupplierTypeChangePopup()
                                            if (checked) {
                                                setSupplierType('L')
                                            } else {
                                                setSupplierType('P')
                                            }
                                        }}
                                        disabled={props.contractAction}
                                    />
                                </div>
                                <div className="flex flex-col min-w-[200px]">
                                    <Checkbox
                                        label="Физическое лицо"
                                        checked={
                                            form.supplier_type === 'P' || false
                                        }
                                        onChange={(checked) => {
                                            handleContractSupplierTypeChangePopup()
                                            if (checked) {
                                                setSupplierType('P')
                                            } else {
                                                setSupplierType('L')
                                            }
                                        }}
                                        disabled={props.contractAction}
                                    />
                                </div>
                            </div>

                            <div className="flex flex-1 items-center justify-start text-[#181818] dark:text-[#FFFFFF] ">
                                <div className="flex flex-col w-full">
                                    <Checkbox
                                        label="Поставщик - нерезидент РФ"
                                        checked={
                                            form.supplier_non_resident || false
                                        }
                                        onChange={(checked) => {
                                            handleContractSupplierNonResidentChangePopup()
                                            setSupplierNonResident(checked)
                                        }}
                                        disabled={props.contractAction}
                                    />
                                </div>
                            </div>

                            {form.supplier_type == 'P' &&
                                form.supplier_provider == false &&
                                form.supplier_non_resident == false && (
                                    <div className="flex flex-1 items-center justify-start text-[#181818] dark:text-[#FFFFFF] ">
                                        <div className="flex flex-col w-full">
                                            <Checkbox
                                                label="Физическое лицо, не являющееся индивидуальным предпринимателем и применяющее специальный налоговый режим «Налог на профессиональный доход»"
                                                checked={
                                                    form.supplier_self_employed ||
                                                    false
                                                }
                                                onChange={(checked) => {
                                                    setForm((prev) => ({
                                                        ...prev,
                                                        supplier_self_employed:
                                                            checked,
                                                    }))
                                                }}
                                                disabled={props.contractAction}
                                            />
                                        </div>
                                    </div>
                                )}

                            {(form.supplier_type == 'L' ||
                                form.supplier_self_employed == false) && (
                                <div className="flex flex-1 items-center justify-start text-[#181818] dark:text-[#FFFFFF] ">
                                    <div className="flex flex-col w-full">
                                        <Checkbox
                                            label="Поставщик относится к субъектам малого и среднего предпринимательства"
                                            checked={
                                                form.supplier_provider || false
                                            }
                                            onChange={(checked) => {
                                                if (checked) {
                                                    setForm((prev) => ({
                                                        ...prev,
                                                        supplier_provider:
                                                            checked,
                                                        supplier_provider_code:
                                                            '30',
                                                    }))
                                                } else {
                                                    setForm((prev) => ({
                                                        ...prev,
                                                        supplier_provider:
                                                            checked,
                                                        supplier_provider_code:
                                                            null,
                                                        supplier_provider_include_msp_date:
                                                            null,
                                                    }))
                                                }
                                            }}
                                            disabled={props.contractAction}
                                        />
                                    </div>
                                </div>
                            )}

                            {form.supplier_provider && (
                                <div className="flex flex-col w-[200px]">
                                    <label className="w-full text-sm text-[#6A6A6A] dark:text-[#8A8A8A]">
                                        Дата вкл. в реестр МСП
                                    </label>

                                    <input
                                        type="date"
                                        className={`form-input user-input-placeholder w-[200px] ${errorClass(
                                            !!props.errors
                                                ?.supplier_provider_include_msp_date
                                        )}`}
                                        value={
                                            form.supplier_provider_include_msp_date ??
                                            ''
                                        }
                                        onChange={(e) => {
                                            e.target.value == ''
                                                ? setForm((prev) => ({
                                                      ...prev,
                                                      supplier_provider_include_msp_date:
                                                          null,
                                                  }))
                                                : setForm((prev) => ({
                                                      ...prev,
                                                      supplier_provider_include_msp_date:
                                                          e.target.value,
                                                  }))
                                        }}
                                        style={{
                                            colorScheme:
                                                props.theme == 'dark'
                                                    ? 'dark'
                                                    : 'light',
                                        }}
                                        disabled={props.contractAction}
                                    />
                                </div>
                            )}

                            <div className="flex flex-1 items-center justify-start text-[#181818] dark:text-[#FFFFFF] ">
                                <div className="flex flex-col w-full">
                                    <Checkbox
                                        label="Поставщик состоит на учете в налоговых органах РФ"
                                        checked={form.supplier_tax || false}
                                        onChange={(checked) => {
                                            setForm((prev) => ({
                                                ...prev,
                                                supplier_tax: checked,
                                            }))
                                        }}
                                        disabled={
                                            !form.supplier_non_resident ||
                                            false ||
                                            props.contractAction
                                        }
                                    />
                                </div>
                            </div>

                            {form.supplier_type == 'L' && (
                                <>
                                    <div className="flex flex-col w-full">
                                        <label className="w-full text-sm text-[#6A6A6A] dark:text-[#8A8A8A]">
                                            Полное наименование
                                        </label>
                                        <input
                                            type="text"
                                            className={`form-input flex pl-2 pr-9 w-full h-[28px] rounded-md text-[#181818] dark:text-[#FFFFFF] ${errorClass(
                                                !!props.errors?.supplier_name
                                            )}`}
                                            value={form.supplier_name || ''}
                                            onChange={(e) => {
                                                setForm((prev) => ({
                                                    ...prev,
                                                    supplier_name:
                                                        e.target.value,
                                                }))
                                            }}
                                            disabled={props.contractAction}
                                        />
                                    </div>
                                    <div className="flex flex-col w-full">
                                        <label className="w-full text-sm text-[#6A6A6A] dark:text-[#8A8A8A]">
                                            Сокращенное наименование
                                        </label>
                                        <input
                                            type="text"
                                            className="form-input flex pl-2 pr-9 w-full h-[28px] rounded-md text-[#181818] dark:text-[#FFFFFF] ..."
                                            value={
                                                form.supplier_short_name || ''
                                            }
                                            onChange={(e) => {
                                                setForm((prev) => ({
                                                    ...prev,
                                                    supplier_short_name:
                                                        e.target.value,
                                                }))
                                            }}
                                            disabled={props.contractAction}
                                        />
                                    </div>
                                    <div className="flex flex-col w-full">
                                        <label className="w-full text-sm text-[#6A6A6A] dark:text-[#8A8A8A]">
                                            Фирменное наименование
                                        </label>
                                        <input
                                            type="text"
                                            className="form-input flex pl-2 pr-9 w-full h-[28px] rounded-md text-[#181818] dark:text-[#FFFFFF] ..."
                                            value={
                                                form.supplier_brand_name || ''
                                            }
                                            onChange={(e) => {
                                                setForm((prev) => ({
                                                    ...prev,
                                                    supplier_brand_name:
                                                        e.target.value,
                                                }))
                                            }}
                                            disabled={props.contractAction}
                                        />
                                    </div>
                                </>
                            )}

                            {form.supplier_non_resident == true && (
                                <div className="flex flex-col w-full">
                                    <label className="w-full text-sm text-[#6A6A6A] dark:text-[#8A8A8A]">
                                        Дополнительное наименование
                                    </label>
                                    <input
                                        type="text"
                                        className="form-input flex pl-2 pr-9 w-full h-[28px] rounded-md text-[#181818] dark:text-[#FFFFFF] ..."
                                        value={
                                            form.supplier_additional_full_name ||
                                            ''
                                        }
                                        onChange={(e) => {
                                            setForm((prev) => ({
                                                ...prev,
                                                supplier_additional_full_name:
                                                    e.target.value,
                                            }))
                                        }}
                                        disabled={props.contractAction}
                                    />
                                </div>
                            )}

                            {form.supplier_type == 'P' && (
                                <div className="flex flex-col w-full">
                                    <label className="w-full text-sm text-[#6A6A6A] dark:text-[#8A8A8A]">
                                        ФИО
                                    </label>
                                    <input
                                        type="text"
                                        className={`form-input flex pl-2 pr-9 w-full h-[28px] rounded-md text-[#181818] dark:text-[#FFFFFF] ${errorClass(
                                            !!props.errors?.supplier_name
                                        )}`}
                                        value={form.supplier_name || ''}
                                        onChange={(e) => {
                                            setForm((prev) => ({
                                                ...prev,
                                                supplier_name: e.target.value,
                                                supplier_short_name:
                                                    e.target.value,
                                                supplier_brand_name: null,
                                            }))
                                        }}
                                        disabled={props.contractAction}
                                    />
                                </div>
                            )}

                            {form.supplier_non_resident == false &&
                                form.supplier_type == 'L' && (
                                    <div className="flex flex-row w-full gap-3">
                                        <div className="flex flex-col w-[200px]">
                                            <label className="w-full text-sm text-[#6A6A6A] dark:text-[#8A8A8A]">
                                                ОКОПФ
                                            </label>
                                            <input
                                                ref={okopfInputRef}
                                                data-autocomplete="okopf"
                                                className={`form-input px-2 rounded-md bg-[#181818] w-full ${errorClass(
                                                    !!props.errors
                                                        ?.supplier_okopf
                                                )}`}
                                                value={okopfQuery}
                                                onChange={(e) => {
                                                    okopfPickedRef.current =
                                                        false
                                                    if (e.target.value == '') {
                                                        onOkopfInput('')
                                                    } else {
                                                        onOkopfInput(
                                                            e.target.value
                                                        )
                                                    }
                                                }}
                                                disabled={props.contractAction}
                                            />
                                        </div>
                                        <div className="flex flex-col w-full">
                                            <label className="w-full text-sm text-[#6A6A6A] dark:text-[#8A8A8A]">
                                                Наименование ОКОПФ
                                            </label>
                                            <input
                                                type="text"
                                                className="form-input flex pl-2 pr-9 w-full h-[28px] rounded-md text-sm text-[#181818] dark:text-[#FFFFFF] bg-[#FFFFFF] dark:bg-[#181818] focus:outline-none focus:ring-0"
                                                value={
                                                    form.supplier_okopf_name ||
                                                    '-'
                                                }
                                                disabled={true}
                                                readOnly
                                            />
                                        </div>
                                        <OkopfDropdown
                                            anchorEl={okopfInputRef.current}
                                            open={okopfOpen}
                                            onClose={() => {
                                                setOkopfOpen(false)
                                                if (!okopfPickedRef.current) {
                                                    setOkopfQuery(
                                                        okopfPrevRef.current
                                                    )
                                                }
                                            }}
                                            items={okopfItems}
                                            theme={props.theme}
                                            onPick={(it) => {
                                                // ← сюда придёт выбранный элемент
                                                okopfPickedRef.current = true
                                                okopfPrevRef.current = it.code
                                                setOkopfOpen(false)
                                                setOkopfQuery(it.code)
                                                setField(
                                                    'supplier_okopf',
                                                    it.code
                                                )
                                                setForm((prev) => ({
                                                    ...prev,
                                                    supplier_okopf: it.code,
                                                    supplier_okopf_name:
                                                        it.name,
                                                }))
                                            }}
                                        />
                                    </div>
                                )}

                            <div className="flex flex-row w-full gap-x-5 ">
                                {(form.supplier_non_resident == false ||
                                    form.supplier_tax == true) && (
                                    <div className="flex flex-col w-[200px]">
                                        <label className="w-full text-sm text-[#6A6A6A] dark:text-[#8A8A8A]">
                                            ИНН
                                        </label>
                                        <input
                                            type="text"
                                            className={`form-input flex pl-2 pr-9 w-full h-[28px] rounded-md text-[#181818] dark:text-[#FFFFFF] ${errorClass(
                                                !!props.errors?.supplier_inn
                                            )}`}
                                            value={form.supplier_inn || ''}
                                            onChange={(e) => {
                                                setForm((prev) => ({
                                                    ...prev,
                                                    supplier_inn:
                                                        e.target.value,
                                                }))
                                            }}
                                            disabled={props.contractAction}
                                        />
                                    </div>
                                )}

                                {form.supplier_type == 'L' &&
                                    (form.supplier_non_resident == false ||
                                        form.supplier_tax == true) && (
                                        <div className="flex flex-col w-[200px]">
                                            <label className="w-full text-sm text-[#6A6A6A] dark:text-[#8A8A8A]">
                                                КПП
                                            </label>
                                            <input
                                                type="text"
                                                className={`form-input flex pl-2 pr-9 w-full h-[28px] rounded-md text-[#181818] dark:text-[#FFFFFF] ${errorClass(
                                                    !!props.errors?.supplier_kpp
                                                )}`}
                                                value={form.supplier_kpp || ''}
                                                onChange={(e) => {
                                                    setForm((prev) => ({
                                                        ...prev,
                                                        supplier_kpp:
                                                            e.target.value,
                                                    }))
                                                }}
                                                disabled={props.contractAction}
                                            />
                                        </div>
                                    )}
                            </div>

                            <div className="flex flex-row w-full gap-x-5 ">
                                {form.supplier_type == 'L' &&
                                    form.supplier_non_resident == false && (
                                        <div className="flex flex-col w-[200px]">
                                            <label className="w-full text-sm text-[#6A6A6A] dark:text-[#8A8A8A]">
                                                ОКПО
                                            </label>
                                            <input
                                                type="text"
                                                className={`form-input flex pl-2 pr-9 w-full h-[28px] rounded-md text-[#181818] dark:text-[#FFFFFF] ${errorClass(
                                                    !!props.errors
                                                        ?.supplier_okpo
                                                )}`}
                                                value={form.supplier_okpo || ''}
                                                onChange={(e) => {
                                                    setForm((prev) => ({
                                                        ...prev,
                                                        supplier_okpo:
                                                            e.target.value,
                                                    }))
                                                }}
                                                disabled={props.contractAction}
                                            />
                                        </div>
                                    )}

                                {form.supplier_tax && (
                                    <div className="flex flex-col w-[200px]">
                                        <label className="w-full text-sm text-[#6A6A6A] dark:text-[#8A8A8A]">
                                            Дата постановки на учет
                                        </label>

                                        <input
                                            type="date"
                                            className={`form-input user-input-placeholder w-[200px] ${errorClass(
                                                !!props.errors
                                                    ?.supplier_registration_date
                                            )}`}
                                            value={
                                                form.supplier_registration_date ??
                                                ''
                                            }
                                            onChange={(e) => {
                                                e.target.value == ''
                                                    ? setForm((prev) => ({
                                                          ...prev,
                                                          supplier_registration_date:
                                                              null,
                                                      }))
                                                    : setForm((prev) => ({
                                                          ...prev,
                                                          supplier_registration_date:
                                                              e.target.value,
                                                      }))
                                            }}
                                            style={{
                                                colorScheme:
                                                    props.theme == 'dark'
                                                        ? 'dark'
                                                        : 'light',
                                            }}
                                            disabled={props.contractAction}
                                        />
                                    </div>
                                )}
                            </div>

                            {form.supplier_non_resident == true && (
                                <div className="flex flex-col w-full">
                                    <label className="w-full text-sm text-[#6A6A6A] dark:text-[#8A8A8A]">
                                        Идентификационный код налогоплательщика
                                    </label>
                                    <input
                                        type="text"
                                        className={`form-input flex pl-2 pr-9 w-full h-[28px] rounded-md text-[#181818] dark:text-[#FFFFFF] ${errorClass(
                                            !!props.errors?.supplier_code
                                        )}`}
                                        value={form.supplier_code || ''}
                                        onChange={(e) => {
                                            setForm((prev) => ({
                                                ...prev,
                                                supplier_code: e.target.value,
                                            }))
                                        }}
                                        disabled={props.contractAction}
                                    />
                                </div>
                            )}
                        </div>
                    </div>

                    <div className="flex flex-col w-[580px] gap-y-5">
                        <h2 className="text-[#181818] dark:text-[#FFFFFF] text-[16px]">
                            Адрес местонахождения
                        </h2>
                        <div className="flex flex-col w-full gap-3">
                            <div className="flex flex-col w-full">
                                <label className="w-full text-sm text-[#6A6A6A] dark:text-[#8A8A8A]">
                                    Почтовый индекс
                                </label>
                                <input
                                    type="text"
                                    className={`form-input flex pl-2 pr-9 w-full h-[28px] rounded-md text-[#181818] dark:text-[#FFFFFF] ${errorClass(
                                        !!props.errors
                                            ?.supplier_address_post_code
                                    )}`}
                                    value={
                                        form.supplier_address_post_code || ''
                                    }
                                    onChange={(e) => {
                                        setForm((prev) => ({
                                            ...prev,
                                            supplier_address_post_code:
                                                e.target.value,
                                        }))
                                    }}
                                    disabled={props.contractAction}
                                />
                            </div>
                            <div className="flex flex-row w-full gap-3">
                                <div className="flex flex-col w-[200px]">
                                    <label className="w-full text-sm text-[#6A6A6A] dark:text-[#8A8A8A]">
                                        Страна
                                    </label>
                                    <select
                                        className={`form-input px-2 rounded-md w-full ${errorClass(
                                            !!props.errors
                                                ?.supplier_address_country
                                        )}`}
                                        value={
                                            form.supplier_address_digital_code ??
                                            ''
                                        }
                                        onChange={(e) => {
                                            const selectedCode = e.target.value
                                            const selectedOkei =
                                                props.oksmList.find(
                                                    (oksm) =>
                                                        oksm.digital_code ===
                                                        selectedCode
                                                )

                                            setForm((prev) => ({
                                                ...prev,
                                                supplier_address_digital_code:
                                                    selectedOkei?.digital_code ??
                                                    null,
                                                supplier_address_country_name:
                                                    selectedOkei?.short_name ??
                                                    null,
                                            }))
                                        }}
                                        disabled={props.contractAction}
                                    >
                                        <option
                                            className="text-[#6A6A6A] dark:text-[#8A8A8A]"
                                            value=""
                                        >
                                            {form.supplier_address_digital_code ==
                                            null
                                                ? 'Выбрать'
                                                : ''}
                                        </option>

                                        {props.oksmList.map((item, index) => (
                                            <option
                                                key={index}
                                                value={item.digital_code}
                                            >
                                                {item.short_name}
                                            </option>
                                        ))}
                                    </select>
                                </div>
                                {form.supplier_non_resident == false && (
                                    <div className="flex flex-col w-full">
                                        <label className="w-full text-sm text-[#6A6A6A] dark:text-[#8A8A8A]">
                                            Субъект РФ
                                        </label>
                                        <select
                                            className={`form-input px-2 rounded-md w-full ${errorClass(
                                                !!props.errors
                                                    ?.supplier_address_region
                                            )}`}
                                            value={
                                                form.supplier_address_region_code ??
                                                ''
                                            }
                                            onChange={(e) => {
                                                const selectedCode =
                                                    e.target.value
                                                const selectedRegionRf =
                                                    props.regionRfList.find(
                                                        (regionRf) =>
                                                            regionRf.code ===
                                                            selectedCode
                                                    )

                                                setForm((prev) => ({
                                                    ...prev,
                                                    supplier_address_region_code:
                                                        selectedRegionRf?.code_eis ??
                                                        null,
                                                    supplier_address_region_name:
                                                        selectedRegionRf?.name_eis ??
                                                        null,
                                                }))
                                            }}
                                            disabled={props.contractAction}
                                        >
                                            <option
                                                className="text-[#6A6A6A] dark:text-[#8A8A8A]"
                                                value=""
                                            >
                                                {form.supplier_address_region_code ==
                                                null
                                                    ? 'Выбрать'
                                                    : ''}
                                            </option>

                                            {props.regionRfList.map(
                                                (item, index) => (
                                                    <option
                                                        key={index}
                                                        value={item.code_eis}
                                                    >
                                                        {item.name_eis}
                                                    </option>
                                                )
                                            )}
                                        </select>
                                    </div>
                                )}
                            </div>
                            {form.supplier_non_resident == false && (
                                <>
                                    <div className="flex flex-col w-full">
                                        <label className="w-full text-sm text-[#6A6A6A] dark:text-[#8A8A8A]">
                                            Муниципальный район, городской
                                            округ, внутригородская территория в
                                            составе субъекта РФ
                                        </label>
                                        <input
                                            type="text"
                                            className={`form-input flex pl-2 pr-9 w-full h-[28px] rounded-md text-[#181818] dark:text-[#FFFFFF] ${errorClass(
                                                !!props.errors
                                                    ?.supplier_address_area
                                            )}`}
                                            value={
                                                form.supplier_address_area || ''
                                            }
                                            onChange={(e) => {
                                                setForm((prev) => ({
                                                    ...prev,
                                                    supplier_address_area:
                                                        e.target.value,
                                                }))
                                            }}
                                            disabled={props.contractAction}
                                        />
                                    </div>
                                    <div className="flex flex-col w-full">
                                        <label className="w-full text-sm text-[#6A6A6A] dark:text-[#8A8A8A]">
                                            Городское или сельское поселение в
                                            составе муниципального района или
                                            внутригородского района городского
                                            округа
                                        </label>
                                        <input
                                            type="text"
                                            className="form-input flex pl-2 pr-9 w-full h-[28px] rounded-md text-[#181818] dark:text-[#FFFFFF] ..."
                                            value={
                                                form.supplier_address_city || ''
                                            }
                                            onChange={(e) => {
                                                setForm((prev) => ({
                                                    ...prev,
                                                    supplier_address_city:
                                                        e.target.value,
                                                }))
                                            }}
                                            disabled={props.contractAction}
                                        />
                                    </div>
                                </>
                            )}

                            {form.supplier_non_resident == true && (
                                <>
                                    <div className="flex flex-col w-full">
                                        <label className="w-full text-sm text-[#6A6A6A] dark:text-[#8A8A8A]">
                                            Элемент административного устройства
                                            страны
                                        </label>
                                        <input
                                            type="text"
                                            className="form-input flex pl-2 pr-9 w-full h-[28px] rounded-md text-[#181818] dark:text-[#FFFFFF] ..."
                                            value={
                                                form.supplier_address_admin_division ||
                                                ''
                                            }
                                            onChange={(e) => {
                                                setForm((prev) => ({
                                                    ...prev,
                                                    supplier_address_admin_division:
                                                        e.target.value,
                                                }))
                                            }}
                                            disabled={props.contractAction}
                                        />
                                    </div>
                                    <div className="flex flex-col w-full">
                                        <label className="w-full text-sm text-[#6A6A6A] dark:text-[#8A8A8A]">
                                            Населенный пункт
                                        </label>
                                        <input
                                            type="text"
                                            className="form-input flex pl-2 pr-9 w-full h-[28px] rounded-md text-[#181818] dark:text-[#FFFFFF] ..."
                                            value={
                                                form.supplier_address_settlement ||
                                                ''
                                            }
                                            onChange={(e) => {
                                                setForm((prev) => ({
                                                    ...prev,
                                                    supplier_address_settlement:
                                                        e.target.value,
                                                }))
                                            }}
                                            disabled={props.contractAction}
                                        />
                                    </div>
                                </>
                            )}

                            {form.supplier_non_resident == false && (
                                <div className="flex flex-row w-full gap-3">
                                    <div className="flex flex-col w-[200px]">
                                        <label className="w-full text-sm text-[#6A6A6A] dark:text-[#8A8A8A]">
                                            ОКТМО
                                        </label>
                                        <input
                                            ref={oktmoInputRef}
                                            data-autocomplete="oktmo"
                                            className={`form-input px-2 rounded-md bg-[#181818] w-full`}
                                            value={oktmoQuery}
                                            onChange={(e) => {
                                                oktmoPickedRef.current = false
                                                if (e.target.value == '') {
                                                    onOktmoInput('')
                                                } else {
                                                    onOktmoInput(e.target.value)
                                                }
                                            }}
                                            disabled={props.contractAction}
                                        />
                                    </div>
                                    <div className="flex flex-col w-full">
                                        <label className="w-full text-sm text-[#6A6A6A] dark:text-[#8A8A8A]">
                                            Наименование ОКТМО
                                        </label>
                                        <input
                                            type="text"
                                            className="form-input flex pl-2 pr-9 w-full h-[28px] rounded-md text-sm text-[#181818] dark:text-[#FFFFFF] bg-[#FFFFFF] dark:bg-[#181818] focus:outline-none focus:ring-0"
                                            value={
                                                form.supplier_address_oktmo_name ||
                                                '-'
                                            }
                                            disabled={true}
                                            readOnly
                                        />
                                    </div>
                                    <OktmoDropdown
                                        anchorEl={oktmoInputRef.current}
                                        open={oktmoOpen}
                                        onClose={() => {
                                            setOktmoOpen(false)
                                            if (!oktmoPickedRef.current) {
                                                setOktmoQuery(
                                                    oktmoPrevRef.current
                                                )
                                            }
                                        }}
                                        items={oktmoItems}
                                        theme={props.theme}
                                        onPick={(it) => {
                                            // ← сюда придёт выбранный элемент
                                            oktmoPickedRef.current = true
                                            oktmoPrevRef.current = it.code
                                            setOktmoOpen(false)
                                            setOktmoQuery(it.code)
                                            setField(
                                                'supplier_address_oktmo',
                                                it.code
                                            )
                                            setForm((prev) => ({
                                                ...prev,
                                                supplier_address_oktmo: it.code,
                                                supplier_address_oktmo_name:
                                                    it.name,
                                            }))
                                        }}
                                    />
                                </div>
                            )}

                            <div className="flex flex-col w-full">
                                <label className="w-full text-sm text-[#6A6A6A] dark:text-[#8A8A8A]">
                                    Элемент планировочной структуры
                                </label>
                                <input
                                    type="text"
                                    className="form-input flex pl-2 pr-9 w-full h-[28px] rounded-md text-[#181818] dark:text-[#FFFFFF] ..."
                                    value={
                                        form.supplier_address_planning_structure ||
                                        ''
                                    }
                                    onChange={(e) => {
                                        setForm((prev) => ({
                                            ...prev,
                                            supplier_address_planning_structure:
                                                e.target.value,
                                        }))
                                    }}
                                    disabled={props.contractAction}
                                />
                            </div>
                            <div className="flex flex-col w-full">
                                <label className="w-full text-sm text-[#6A6A6A] dark:text-[#8A8A8A]">
                                    Элемент улично-дорожной сети (улица)
                                </label>
                                <input
                                    type="text"
                                    className="form-input flex pl-2 pr-9 w-full h-[28px] rounded-md text-[#181818] dark:text-[#FFFFFF] ..."
                                    value={form.supplier_address_street || ''}
                                    onChange={(e) => {
                                        setForm((prev) => ({
                                            ...prev,
                                            supplier_address_street:
                                                e.target.value,
                                        }))
                                    }}
                                    disabled={props.contractAction}
                                />
                            </div>
                            <div className="flex flex-col w-full">
                                <label className="w-full text-sm text-[#6A6A6A] dark:text-[#8A8A8A]">
                                    Тип и номер здания, сооружения
                                </label>
                                <input
                                    type="text"
                                    className={`form-input flex pl-2 pr-9 w-full h-[28px] rounded-md text-[#181818] dark:text-[#FFFFFF] ${errorClass(
                                        !!props.errors?.supplier_address_house
                                    )}`}
                                    value={form.supplier_address_house || ''}
                                    onChange={(e) => {
                                        setForm((prev) => ({
                                            ...prev,
                                            supplier_address_house:
                                                e.target.value,
                                        }))
                                    }}
                                    disabled={props.contractAction}
                                />
                            </div>
                            <div className="flex flex-col w-full">
                                <label className="w-full text-sm text-[#6A6A6A] dark:text-[#8A8A8A]">
                                    Тип и номер помещения, расположенного в
                                    здании, сооружении
                                </label>
                                <input
                                    type="text"
                                    className="form-input flex pl-2 pr-9 w-full h-[28px] rounded-md text-[#181818] dark:text-[#FFFFFF] ..."
                                    value={form.supplier_address_office || ''}
                                    onChange={(e) => {
                                        setForm((prev) => ({
                                            ...prev,
                                            supplier_address_office:
                                                e.target.value,
                                        }))
                                    }}
                                    disabled={props.contractAction}
                                />
                            </div>
                            <div className="flex flex-col w-full">
                                <label className="w-full text-sm text-[#6A6A6A] dark:text-[#8A8A8A]">
                                    Номера телефонов
                                </label>
                                <input
                                    type="text"
                                    className={`form-input flex pl-2 pr-9 w-full h-[28px] rounded-md text-[#181818] dark:text-[#FFFFFF] ${errorClass(
                                        !!props.errors?.supplier_address_phone
                                    )}`}
                                    value={form.supplier_address_phone || ''}
                                    onChange={(e) => {
                                        setForm((prev) => ({
                                            ...prev,
                                            supplier_address_phone:
                                                e.target.value,
                                        }))
                                    }}
                                    disabled={props.contractAction}
                                />
                            </div>
                            <div className="flex flex-col w-full">
                                <label className="w-full text-sm text-[#6A6A6A] dark:text-[#8A8A8A]">
                                    Адреса электронной почты
                                </label>
                                <input
                                    type="text"
                                    className={`form-input flex pl-2 pr-9 w-full h-[28px] rounded-md text-[#181818] dark:text-[#FFFFFF] ${errorClass(
                                        !!props.errors?.supplier_address_email
                                    )}`}
                                    value={form.supplier_address_email || ''}
                                    onChange={(e) => {
                                        setForm((prev) => ({
                                            ...prev,
                                            supplier_address_email:
                                                e.target.value,
                                        }))
                                    }}
                                    disabled={props.contractAction}
                                />
                            </div>
                        </div>
                    </div>
                </div>
                <div className="flex flex-row text-right justify-end items-center gap-2">
                    <button
                        className="flex flex-row min-w-[120px] h-6 px-4 rounded-[10px] justify-center items-center gap-2 text-xs shadow-md shadow-[#18181830] text-[#181818] dark:text-[#FFFFFF] bg-[#f6f6f6] dark:bg-[#494849] hover:bg-[#FFFFFF] dark:hover:bg-[#656365]"
                        onClick={props.handleContractSupplierEditPopup}
                        disabled={false}
                    >
                        Отмена
                    </button>
                    {!props.contractAction && (
                        <button
                            className="flex flex-row min-w-[120px] h-6 px-4 rounded-[10px] justify-center items-center gap-2 text-xs shadow-md shadow-[#18181830] text-[#181818] dark:text-[#FFFFFF] bg-[#83d2b7] hover:bg-[#8ce5c6] dark:bg-[#187A33] dark:hover:bg-[#1D9A40]"
                            onClick={handleSave}
                            disabled={isSaving}
                        >
                            Сохранить
                        </button>
                    )}
                </div>
            </div>

            {contractSupplierTypeChangePopup && (
                <div className="fixed top-0 left-0 w-full h-full bg-[#00000050] flex justify-center items-center">
                    <div
                        className="p-5 rounded-lg bg-[#ebebeb] dark:bg-[#262427] w-fit max-h-[600px]"
                        onClick={(e) => e.stopPropagation()}
                    >
                        <div className="flex flex-col text-sm text-[#181818] dark:text-[#FFFFFF] items-center justify-center gap-5">
                            <div className="flex flex-col">
                                <label className="flex mb-1 text-sm text-[#181818] dark:text-[#FFFFFF]">
                                    Изменить тип контрагента? <br />
                                    Ранее сохраненные сведения будут потеряны.
                                </label>
                            </div>
                            <div className="flex w-full flex-row items-center justify-end gap-3">
                                <button
                                    className="flex flex-row min-w-[120px] h-6 px-4 rounded-[10px] justify-center items-center gap-2 text-xs shadow-md shadow-[#18181830] text-[#181818] dark:text-[#FFFFFF] bg-[#f6f6f6] dark:bg-[#494849] hover:bg-[#FFFFFF] dark:hover:bg-[#656365]"
                                    onClick={
                                        handleContractSupplierTypeChangePopup
                                    }
                                    disabled={false}
                                >
                                    Отмена
                                </button>
                                <button
                                    className="flex px-4 py-1 min-w-[120px] h-6 rounded-[10px] items-center justify-center text-xs shadow-md shadow-[#18181830] text-[#181818] bg-[#ed9cae] hover:bg-[#ffa7ba] dark:bg-[#701F1F] dark:hover:bg-[#9e2c2c] dark:text-[#FFFFFF]"
                                    onClick={handleChangeContractSupplierType}
                                >
                                    Изменить
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            )}
            {contractSupplierNonResidentChangePopup && (
                <div className="fixed top-0 left-0 w-full h-full bg-[#00000050] flex justify-center items-center">
                    <div
                        className="p-5 rounded-lg bg-[#e5e5e5] dark:bg-[#262427] w-fit max-h-[600px]"
                        onClick={(e) => e.stopPropagation()}
                    >
                        <div className="flex flex-col text-sm text-[#181818] dark:text-[#FFFFFF] items-center justify-center gap-5">
                            <div className="flex flex-col">
                                <label className="flex mb-1 text-sm text-[#181818] dark:text-[#FFFFFF]">
                                    Изменить тип контрагента? <br />
                                    Ранее сохраненные сведения будут потеряны.
                                </label>
                            </div>
                            <div className="flex w-full flex-row items-center justify-end gap-3">
                                <button
                                    className="flex flex-row min-w-[120px] h-6 px-4 rounded-[10px] justify-center items-center gap-2 text-xs shadow-md shadow-[#18181830] text-[#181818] dark:text-[#FFFFFF] bg-[#f6f6f6] dark:bg-[#494849] hover:bg-[#FFFFFF] dark:hover:bg-[#656365]"
                                    onClick={
                                        handleContractSupplierNonResidentChangePopup
                                    }
                                    disabled={false}
                                >
                                    Отмена
                                </button>
                                <button
                                    className="flex px-4 py-1 min-w-[120px] h-6 rounded-[10px] items-center justify-center text-xs shadow-md shadow-[#18181830] text-[#181818] bg-[#ed9cae] hover:bg-[#ffa7ba] dark:bg-[#701F1F] dark:hover:bg-[#9e2c2c] dark:text-[#FFFFFF]"
                                    onClick={
                                        handleChangeContractSupplierNonResident
                                    }
                                >
                                    Изменить
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            )}
        </div>
    )
}
export default ContractSupplierEditPopup
